import React, { useEffect, useState } from 'react'
import { ITableViewColumn, OptionProps } from 'types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getOriginalData } from 'components/spreadsheet/helpers/functions'
import { getFilterValues } from 'components/spreadsheet/helpers/filtering'
import Select from 'components/select'
import { formatDateTime, formatDate } from 'components/spreadsheet/helpers/format'
import { FilterVariables } from 'components/spreadsheet/constants/const'

// This is the expected type of value returned from the API for field values
export type FilterValueType = string | number | boolean | null

interface Props {
  columnId: string
  multipleValues: string[]
  onChange: (options: string[]) => void
  optionsOpen?: boolean
}

// Construct the option label from it's value
const createLabel = (value: FilterValueType, column: ITableViewColumn): string => {
  if (value === undefined || value === null) {
    return ''
  } else {
    if (column.kind === 'datetime') {
      const formatedDate = formatDateTime((value as string) + 'Z', 'YYYY-MM-DDTHH:mm')
      return formatedDate
    } else if (column.kind === 'date') {
      const formatedDate = formatDate(value as string, 'YYYY-MM-DD')
      return formatedDate
    } else {
      return value.toString()
    }
  }
}

/** The values a column holds (and any picked that no longer exist), as options for a "one of" filter. */
export const useFieldValueOptions = (
  columnId: string,
  multipleValues: string[]
): { options: OptionProps[]; isLoaded: boolean } => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [options, setOptions] = useState<OptionProps[]>([])
  const { spreadsheetData } = useDataContext()

  const buildOptions = () => {
    /* -------------------------------------------------
     * 1. collect distinct values that still exist
     * With bitmap model, all rows (visible + filtered) are in spreadsheetData.rows
     * In transpose view, filters reference original columns so use original data
     * ------------------------------------------------- */
    const origData = getOriginalData(spreadsheetData)
    const column = origData.columns.find((c) => c.publicId === columnId)
    if (!column) {
      setIsLoaded(true)
      return
    }
    const allValues = [
      ...getFilterValues(origData.rows, column, columnId, origData.columnIdToIndex, origData.columnStore)
    ]
    const values = new Set(allValues)

    /* -------------------------------------------------
     * 2. turn them into <Select> options
     * ------------------------------------------------- */
    const selectOptions: OptionProps[] = Array.from(values).map((v: any) => ({
      value: v !== null && v !== undefined ? v.toString() : '',
      label: createLabel(v, column)
    }))

    /* -------------------------------------------------
     * 3. make sure *selected but missing* values
     *    also have an option (prevents blank chips)
     * ------------------------------------------------- */
    multipleValues.forEach((v) => {
      if (!selectOptions.some((o) => o.value === v)) {
        // `multipleValues` are already strings – just echo them
        selectOptions.push({ value: v, label: v })
      }
    })

    /* -------------------------------------------------
     * 4. add admin macros, sort, and save
     * ------------------------------------------------- */
    selectOptions.sort((a, b) => a.label.localeCompare(b.label))

    if (spreadsheetData.isAdmin) {
      FilterVariables.forEach((variable) => {
        if (!selectOptions.some((o) => o.value === variable)) {
          selectOptions.push({ value: variable, label: variable })
        }
      })
    }

    setOptions(selectOptions)
    setIsLoaded(true)
  }

  useEffect(() => {
    buildOptions()
  }, [columnId, spreadsheetData.rows, spreadsheetData.isAdmin, spreadsheetData.viewDetails.columns, multipleValues])

  return { options, isLoaded }
}

// For filter, this will fetch unique values from the backend for the column the
// filter is assigned to, and present that in a multi select dropdown UI component
const FieldValueMultiPickerInput: React.FC<Props> = ({ columnId, multipleValues, onChange, optionsOpen }) => {
  const { options, isLoaded } = useFieldValueOptions(columnId, multipleValues)

  return (
    <Select
      options={options}
      optionsSelected={multipleValues}
      onOptionClick={(option) => {
        const newOptions = [...multipleValues]
        const indexOf = newOptions.findIndex((value) => value === option)
        if (indexOf === -1) {
          newOptions.push(option)
        } else {
          newOptions.splice(indexOf, 1)
        }
        onChange(newOptions)
      }}
      multiselect={true}
      loading={!isLoaded}
      defaultOpen={optionsOpen}
    />
  )
}

export default FieldValueMultiPickerInput
